import { useMemo, useState } from "react";
import DialogVisualizacaoArquivos from "../../SolicitarAnaliseCredito/FormSolicitacao/DialogVisualizacaoArquivos";
import DialogDetalheCredUtilizado from "./DialogDetalheCredUtilizado";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LabelPrinter from "../../../../../components/LabelPrinter";
import minusculoExcetoPrimeira from "../../../../../helpers/minusculoExcetoPrimeira";
import {
  formatarCPFCNPJ,
  formatarValorMonetario,
} from "../../../../../helpers/masks";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import { IDataPublicos } from "../../../../../types/api/geral/pesquisar-publicos";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import LoadingApiQuery from "../../../../../components/LoadingApiQuery";

export default function BodyDisplay({ tipo, codigo }: BodyDisplayProps) {
  const [mostrarConteudo, setMostrarConteudo] = useState<boolean>(false);
  const [openModalViewer, setopenModalViewer] = useState(false);
  const [openLimiteUtilizado, setOpenLimiteUtilizado] = useState(false);

  //querys e mutations
  const { data, isLoading: carregandoPublicos } = useQuery({
    queryKey: [`pesquisar-publicos-${tipo}${codigo}`, tipo, codigo],
    queryFn: () =>
      axiosClient.get("/pesquisar-publicos", {
        params: {
          limit: 5,
          page: 1,
          pesquisar: tipo + codigo,
        },
      }),
    refetchOnWindowFocus: false,
  });
  const defaultValueCliente: IDataPublicos = useMemo(
    () => data?.data.data[0] || [],
    [data]
  );
  //

  //funçoes manipuladoras
  const colorChipSituacao = useMemo(() => {
    const situacao: string = defaultValueCliente.situacao_financeira;

    if (situacao === "LIBERADO") return "success";
    if (situacao === "BLOQUEADO DEFINITIVO") return "error";
    if (situacao === "PENDENCIAS FINANCEIRAS") return "warning";

    return "default";
  }, [defaultValueCliente]);
  //

  return (
    <>
      {carregandoPublicos && <LoadingApiQuery />}

      {!carregandoPublicos && defaultValueCliente && (
        <Box p={2} bgcolor="#e5f6fd" borderRadius={1} mb={1}>
          <Grid container spacing={2}>
            <Grid size={{xs: 12, md: 8}}>
              <Typography fontWeight="bold">
                {defaultValueCliente.nome_pessoa}
              </Typography>
              <Typography>
                {formatarCPFCNPJ(defaultValueCliente.documento_pessoa)}
              </Typography>
            </Grid>

            <Grid
              size={{xs: 12, md: 4}}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
            >
              <Tooltip title="Visualizar documentos existentes">
                <IconButton onClick={() => setopenModalViewer(true)}>
                  <FolderCopyIcon />
                </IconButton>
              </Tooltip>

              <IconButton
                onClick={() => setMostrarConteudo(!mostrarConteudo)}
                aria-label="delete"
              >
                {mostrarConteudo ? <RemoveRedEyeIcon /> : <VisibilityOffIcon />}
              </IconButton>

              <Chip
                label={
                  defaultValueCliente.tipo_publico +
                  defaultValueCliente.codigo_publico
                }
                icon={<AccountCircleIcon />}
              />
            </Grid>

            <Grid size={{xs: 12, md: 3}}>
              <LabelPrinter
                titulo="Limite de crédito atual"
                valor={
                  mostrarConteudo
                    ? formatarValorMonetario(
                        Number(defaultValueCliente.limite_credito)
                      )
                    : "-"
                }
              />
            </Grid>

            <Grid size={{xs: 12, md: 3}}>
              <LabelPrinter
                titulo="Utilizado"
                valor={
                  mostrarConteudo
                    ? formatarValorMonetario(
                        Number(defaultValueCliente.limite_utilizado)
                      )
                    : "-"
                }
              />
              {mostrarConteudo && (
                <Link
                  variant="subtitle2"
                  onClick={() => setOpenLimiteUtilizado(true)}
                  sx={{ cursor: "pointer" }}
                >
                  Ver detalhes
                </Link>
              )}
            </Grid>

            <Grid size={{xs: 8, md: 3}}>
              <LabelPrinter
                titulo="Disponível"
                valor={
                  mostrarConteudo
                    ? formatarValorMonetario(
                        Number(defaultValueCliente.limite_disponivel)
                      )
                    : "-"
                }
              />
            </Grid>

            <Grid size={{xs: 4, md: 3}}>
              <Stack direction="row" justifyContent="flex-end">
                {mostrarConteudo && (
                  <Chip
                    variant="outlined"
                    color={colorChipSituacao}
                    icon={<LocalAtmIcon />}
                    label={
                      minusculoExcetoPrimeira(
                        defaultValueCliente.situacao_financeira
                      ) ?? "-"
                    }
                    sx={{ pl: 1 }}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>

          {openModalViewer && (
            <DialogVisualizacaoArquivos
              open={openModalViewer}
              clienteInfo={{
                codigo: defaultValueCliente.codigo_publico ?? "",
                tipo: defaultValueCliente.tipo_publico,
              }}
              handleClose={() => setopenModalViewer(false)}
            />
          )}

          {openLimiteUtilizado && (
            <DialogDetalheCredUtilizado
              open={openLimiteUtilizado}
              codigo={defaultValueCliente.codigo_publico}
              tipo={defaultValueCliente.tipo_publico}
              handleClose={() => setOpenLimiteUtilizado(false)}
            />
          )}
        </Box>
      )}
    </>
  );
}

interface BodyDisplayProps {
  tipo: string;
  codigo: number;
}
